import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Send, CreditCard } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function RequestCardDialog({ open, onOpenChange, onRequested }) {
  const [nickname, setNickname] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setNickname("");
    setAmount("");
    setNote("");
  };

  const handleRequest = async () => {
    setLoading(true);
    try {
      await api.post("/card-requests", { nickname: nickname.trim(), note: note.trim(), amount: parseFloat(amount) || 0 });
      toast.success("Card request sent to the issuer");
      reset();
      onOpenChange(false);
      onRequested && onRequested();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-[#121216] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#00F0FF]" /> Request a Card
          </DialogTitle>
          <DialogDescription className="text-[#A0A0AB]">
            Ask the network issuer to mint you a card. You'll be notified once it's issued.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Nickname (optional)</Label>
              <Input
                data-testid="request-nickname-input"
                placeholder="@myhandle"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                className="bg-[#1A1A20] border-white/10 text-white h-11 font-mono-sig"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Amount (USD)</Label>
              <Input
                data-testid="request-amount-input"
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-[#1A1A20] border-white/10 text-white h-11 font-mono-sig"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Note to issuer (optional)</Label>
            <Textarea
              data-testid="request-note-input"
              placeholder="Anything the issuer should know…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="bg-[#1A1A20] border-white/10 text-white min-h-[80px]"
            />
          </div>
          <Button
            data-testid="request-submit-button"
            onClick={handleRequest}
            disabled={loading}
            className="w-full h-12 bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold rounded-xl"
          >
            <Send className="w-4 h-4 mr-2" /> {loading ? "Sending…" : "Send request"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
